import {
  ATTENDED_STATUSES,
  MISSED_STATUSES,
  RATE_EXCLUDED_STATUSES,
  type AttendanceStatus,
} from './constants.js';

export interface StatusCounts {
  present: number;
  absent: number;
  late: number;
  excused: number;
}

export const EMPTY_COUNTS: StatusCounts = { present: 0, absent: 0, late: 0, excused: 0 };

export function countStatuses(statuses: readonly AttendanceStatus[]): StatusCounts {
  const counts: StatusCounts = { ...EMPTY_COUNTS };
  for (const status of statuses) counts[status] += 1;
  return counts;
}

const attended = new Set<string>(ATTENDED_STATUSES);
const missed = new Set<string>(MISSED_STATUSES);

export function isAttended(status: AttendanceStatus): boolean {
  return attended.has(status);
}

export function isExcludedFromRate(status: AttendanceStatus): boolean {
  return new Set<string>(RATE_EXCLUDED_STATUSES).has(status);
}

/**
 * The reference implementation of the attendance rate.
 *
 * Returns a fraction in [0, 1], or `null` when the denominator is zero - every
 * mark was excused, or there were no marks at all. `null` rather than 0 because
 * "no data" and "attended nothing" are different facts and a report that
 * conflates them is wrong.
 *
 * The SQL in the reporting queries mirrors this exactly and a test asserts the
 * two agree.
 */
export function attendanceRate(counts: StatusCounts): number | null {
  let numerator = 0;
  let denominator = 0;

  for (const [status, count] of Object.entries(counts) as [AttendanceStatus, number][]) {
    if (isExcludedFromRate(status)) continue;
    denominator += count;
    if (isAttended(status)) numerator += count;
  }

  if (denominator === 0) return null;
  return numerator / denominator;
}

/** Rate as a rounded whole percentage, or `null` when undefined. */
export function attendancePercent(counts: StatusCounts): number | null {
  const rate = attendanceRate(counts);
  return rate === null ? null : Math.round(rate * 100);
}

/** Guards against a status list drifting out of sync with the rate buckets. */
export function assertStatusesPartitioned(): void {
  const all = new Set<string>([...attended, ...missed, ...RATE_EXCLUDED_STATUSES]);
  if (all.size !== 4) {
    throw new Error(`attendance status buckets do not partition cleanly: ${[...all].join(', ')}`);
  }
}
